import { useEffect, useRef } from "react";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import {
  finishedProcessTypes,
  ProcessStatusEnum,
  RydeProcessTitle,
} from "../enums/processes.enum";
import { getActiveProcessKey } from "../helpers/process.helpers";
import { RydeUiUnit } from "../models/common.models";
import { activeProcessSF } from "../recoil/selectors/process.selectors";

interface IUseOnProcessEnd {
  entity: RydeUiUnit;
  processTitle: RydeProcessTitle;
  onProcessEnd: () => void;
}

const isFinished = (status?: ProcessStatusEnum | null) =>
  finishedProcessTypes.includes(status as ProcessStatusEnum);

export const useOnProcessEnd = ({
  entity,
  processTitle,
  onProcessEnd,
}: IUseOnProcessEnd) => {
  const bulkProcessKey = getActiveProcessKey(processTitle, entity);
  const bulkProcess = useRecoilValue(activeProcessSF(bulkProcessKey));

  const pendingRef = useRef(false);
  const onEndRef = useRef(onProcessEnd);
  onEndRef.current = onProcessEnd;

  // call this when you want to start waiting for the process to finish
  const startListening = useEvent(() => {
    pendingRef.current = true;

    // if already finished, fire immediately
    if (isFinished(bulkProcess?.status as ProcessStatusEnum)) {
      pendingRef.current = false;
      onEndRef.current?.();
    }
  });

  useEffect(() => {
    if (
      !pendingRef.current ||
      bulkProcess === null ||
      bulkProcess.status === ProcessStatusEnum.Pending
    ) {
      return;
    }

    if (isFinished(bulkProcess?.status as ProcessStatusEnum)) {
      pendingRef.current = false;
      onEndRef.current?.();
    }
  }, [bulkProcess]);

  return { startListening };
};
